import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/utils/api";
import { services } from "@/data/services";
import { trackEvent } from "@/utils/tracking";
import { trackEvent as internalTrack, getUtmSession } from "@/utils/trackingClient";
import { openWhatsApp as openWhatsAppHelper } from "@/utils/phone";
import { getAttributionPayload } from "@/utils/attribution";

const LeadFormWidget = ({ settings, variant = "default", defaultService, gewerkSlug }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    location: "",
    service: defaultService || "",
    message: ""
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.name || !formData.phone || !formData.location || !formData.service) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }
    if (!consentChecked) {
      toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
      return;
    }

    const phoneRegex = /^[\d\s\+\-\(\)]+$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error("Bitte geben Sie eine gültige Telefonnummer ein.");
      return;
    }

    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const utm = getUtmSession();
      const tracking = getAttributionPayload();
      const response = await api.post('/leads', {
        ...formData,
        email: formData.email || null,
        privacy_consent: true,
        utm_source: utm.utm_source || null,
        utm_medium: utm.utm_medium || null,
        utm_campaign: utm.utm_campaign || null,
        utm_content: utm.utm_content || null,
        utm_term: utm.utm_term || null,
        initial_referrer: utm.initial_referrer || null,
        initial_landing_page: utm.initial_landing_page || null,
        lead_source: 'Schnellformular',
        tracking,
      });

      trackEvent('generate_lead', {
        service: formData.service,
        lead_name: formData.name,
        lead_id: response.data.id,
        has_email: !!formData.email
      });
      internalTrack('form_submit', {
        event_type: 'submit',
        extra: { service: formData.service, lead_id: response.data.id, form: 'schnellformular' },
      });

      // Formular-Conversion feuert erst auf /danke (lead_pending-Sperre gegen Doppelzählung)
      try {
        sessionStorage.setItem('lead_pending', JSON.stringify({
          gewerk: gewerkSlug || null,
          service: formData.service,
          transaction_id: response.data.id,
          phone: formData.phone,
          email: formData.email || null,
          ts: Date.now(),
        }));
      } catch (err) { /* sessionStorage nicht verfügbar */ }

      navigate(`/danke${gewerkSlug ? `?gewerk=${encodeURIComponent(gewerkSlug)}` : ''}`, {
        state: {
          lead_id: response.data.id,
          service: formData.service,
          source: "Schnellformular",
          email: formData.email,
        },
      });
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const whatsappNumber = settings?.whatsapp_number || '4917620283482';
    let message = "Hallo BEKAJ BAU,\nich interessiere mich für eine Bauanfrage.\n\n";
    if (formData.name) message += `Name: ${formData.name}\n`;
    if (formData.service) message += `Leistung: ${formData.service}\n`;
    if (formData.message) message += `\nNachricht: ${formData.message}`;
    openWhatsAppHelper(whatsappNumber, message);
    trackEvent('contact_whatsapp', { source: 'form_widget', service: formData.service });
  };

  if (showSuccess) {
    return (
      <div className="bg-green-50 rounded-xl p-6 border border-green-200 text-center" data-testid="form-success">
        <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-[#4A331D] mb-2">Vielen Dank!</h3>
        <p className="text-gray-700 mb-4 text-sm">Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns in Kürze.</p>
        <Button
          onClick={openWhatsApp}
          className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-6 py-3 rounded-lg font-semibold"
          data-testid="success-whatsapp-btn"
        >
          <MessageCircle className="w-5 h-5 mr-2" />
          Direkt per WhatsApp kontaktieren
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="lead-form-widget">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor={`name-${variant}`} className="font-medium text-[#4A331D] text-sm">Name *</Label>
          <Input
            id={`name-${variant}`}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ihr vollständiger Name"
            required
            className="mt-1 h-11 border-gray-300 focus:border-[#D4AA6A] focus:ring-[#D4AA6A]"
            data-testid={`lead-name-${variant}`}
          />
        </div>

        <div>
          <Label htmlFor={`phone-${variant}`} className="font-medium text-[#4A331D] text-sm">Telefon *</Label>
          <Input
            id={`phone-${variant}`}
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="0176 ..."
            required
            className="mt-1 h-11 border-gray-300 focus:border-[#D4AA6A] focus:ring-[#D4AA6A]"
            data-testid={`lead-phone-${variant}`}
          />
        </div>

        <div>
          <Label htmlFor={`email-${variant}`} className="font-medium text-[#4A331D] text-sm">E-Mail</Label>
          <Input
            id={`email-${variant}`}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="ihre@email.de"
            className="mt-1 h-11 border-gray-300 focus:border-[#D4AA6A] focus:ring-[#D4AA6A]"
            data-testid={`lead-email-${variant}`}
          />
        </div>

        <div>
          <Label htmlFor={`location-${variant}`} className="font-medium text-[#4A331D] text-sm">PLZ / Ort *</Label>
          <Input
            id={`location-${variant}`}
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            placeholder="z. B. 63538 Großkrotzenburg"
            required
            className="mt-1 h-11 border-gray-300 focus:border-[#D4AA6A] focus:ring-[#D4AA6A]"
            data-testid={`lead-location-${variant}`}
          />
        </div>

        <div>
          <Label htmlFor={`service-${variant}`} className="font-medium text-[#4A331D] text-sm">Gewünschte Leistung *</Label>
          <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })}>
            <SelectTrigger className="mt-1 h-11 border-gray-300" data-testid={`lead-service-${variant}`}>
              <SelectValue placeholder="Bitte wählen" />
            </SelectTrigger>
            <SelectContent>
              {services.map((service) => (
                <SelectItem key={service.value} value={service.title}>
                  {service.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor={`message-${variant}`} className="font-medium text-[#4A331D] text-sm">Kurze Nachricht</Label>
          <Textarea
            id={`message-${variant}`}
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Beschreiben Sie kurz Ihr Projekt..."
            rows={3}
            className="mt-1 border-gray-300 focus:border-[#D4AA6A] focus:ring-[#D4AA6A]"
            data-testid={`lead-message-${variant}`}
          />
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            id={`consent-${variant}`}
            checked={consentChecked}
            onCheckedChange={(v) => setConsentChecked(v === true)}
            className="mt-0.5 border-gray-400 data-[state=checked]:bg-[#4A331D] data-[state=checked]:border-[#4A331D]"
            data-testid={`lead-consent-${variant}`}
          />
          <Label htmlFor={`consent-${variant}`} className="text-xs text-gray-600 font-normal leading-snug cursor-pointer">
            Ich habe die{" "}
            <Link to="/datenschutz" className="underline text-[#4A331D] hover:text-[#725029]" target="_blank">
              Datenschutzerklärung
            </Link>{" "}
            gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. *
          </Label>
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#D4AA6A] hover:bg-[#c49a5a] text-[#4A331D] py-6 text-base font-bold rounded-lg shadow-lg hover:shadow-xl transition-all"
          data-testid={`submit-lead-${variant}`}
        >
          {isSubmitting ? "Wird gesendet..." : "Jetzt kostenlose Einschätzung anfordern"}
        </Button>
      </form>
    </div>
  );
};

export default LeadFormWidget;
